import type { ApolloCache, ApolloClient, DocumentNode } from '@apollo/client';
import { normalizeFilter, type ImageFilter, type ImageSelection } from '@arthelper/shared/image-library';

import { IMAGE_IDS_QUERY, IMAGE_ROWS_QUERY } from '~/graphql/library-documents';

/** `myImages` clamps `first` to [1, 100] and `ImageFilter.ids` rejects more than 100. */
export const LIBRARY_PAGE_MAX = 100;

type Row = { id: string; [key: string]: any };

export function chunk<T>(items: readonly T[], size = LIBRARY_PAGE_MAX): T[][] {
  return items.reduce<T[][]>((memo, item, index) => {
    if (index % size === 0) {
      memo.push([]);
    }

    memo[memo.length - 1].push(item);
    return memo;
  }, []);
}

/**
 * Every image matching `filter`, one bounded page at a time until `hasNextPage` is false.
 * `no-cache`: these are one-off reads, and the `myImages` field policy would otherwise
 * merge them into whatever on-screen connection shares the filter.
 */
export async function pageAll(
  client: ApolloClient<any>,
  filter: ImageFilter,
  query: DocumentNode = IMAGE_ROWS_QUERY,
  after: string | null = null,
  rows: Row[] = []
): Promise<Row[]> {
  const { data } = await client.query({
    query,
    variables: { first: LIBRARY_PAGE_MAX, after, filter: normalizeFilter(filter) },
    fetchPolicy: 'no-cache',
  });
  const connection = data.myImages;
  const next = [...rows, ...connection.edges.map((edge: { node: Row }) => edge.node)];
  if (!connection.pageInfo.hasNextPage) {
    return next;
  }

  return pageAll(client, filter, query, connection.pageInfo.endCursor, next);
}

/** The caller's images among `ids`, resolved in sequential chunks of at most 100 ids. */
export function rowsByIds(client: ApolloClient<any>, ids: readonly string[]): Promise<Row[]> {
  return chunk(ids).reduce<Promise<Row[]>>(async (memo, chunkIds) => {
    const rows = await memo;
    const page = await pageAll(client, { ids: chunkIds });
    return [...rows, ...page];
  }, Promise.resolve([]));
}

/** Every image of a folder and its descendant folders. */
export function folderRows(client: ApolloClient<any>, collectionId: string): Promise<Row[]> {
  return pageAll(client, { collectionId, nested: true });
}

/** Ids of every image matching `filter` (unfiltered = the whole library). */
export async function allIds(client: ApolloClient<any>, filter: ImageFilter = {}): Promise<string[]> {
  const rows = await pageAll(client, filter, IMAGE_IDS_QUERY);
  return rows.map((row) => String(row.id));
}

/**
 * Writes `isPublic` onto the cached `Image` rows an explicit selection names, after an
 * `updateImages` that returns only a count left the normalized rows stale. A select-all
 * (`filter`) names no ids and relies on its refetch; a folder's images likewise.
 */
export function writeVisibility(cache: ApolloCache<unknown>, selection: ImageSelection, isPublic: boolean) {
  if (selection.filter) {
    return;
  }

  (selection.ids ?? []).forEach((id) => {
    cache.modify({ id: cache.identify({ __typename: 'Image', id }), fields: { isPublic: () => isPublic } });
  });
}
